if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.scrollIntoViewEnabled = this.dataset.scrollIntoView !== 'false';
      
      // Handle left thumbnails layout
      const leftThumbnails = this.querySelectorAll('.left-thumbnail-item[data-target]');
      if (leftThumbnails.length > 0) {
        // For left thumbnails layout, we don't need the regular thumbnails
        this.elements.thumbnails = null;
        
        leftThumbnails.forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
        });
        
        // Handle navigation arrows
        const prevArrow = this.querySelector('.left-thumbnails-arrow--prev');
        const nextArrow = this.querySelector('.left-thumbnails-arrow--next');
        
        if (prevArrow && nextArrow) {
          prevArrow.addEventListener('click', this.navigateLeftThumbnails.bind(this, 'prev'));
          nextArrow.addEventListener('click', this.navigateLeftThumbnails.bind(this, 'next'));
        }
        
        // Ensure first image is active by default
        const firstLeftThumbnail = this.querySelector('.left-thumbnail-item');
        if (firstLeftThumbnail) {
          this.setActiveLeftThumbnail(firstLeftThumbnail);
          // Also activate the first media item
          const firstMediaId = firstLeftThumbnail.dataset.target;
          if (firstMediaId) {
            this.setActiveMedia(firstMediaId, false, false);
          }
        }
        
        // Update arrow states
        this.updateLeftThumbnailsArrows();
      } else if (this.elements.thumbnails) {
        // Regular thumbnails layout
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
        });
        
        if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      // Handle regular thumbnails
      if (this.elements.thumbnails) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }
      
      // Handle left thumbnails
      const leftThumbnail = this.querySelector(`.left-thumbnail-item[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      if (leftThumbnail) {
        this.setActiveLeftThumbnail(leftThumbnail);
      }
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView && this.scrollIntoViewEnabled) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      // Handle left thumbnails
      const activeLeftThumbnail = this.querySelector(`.left-thumbnail-item[data-target="${ mediaId }"]`);
      if (activeLeftThumbnail) {
        this.setActiveLeftThumbnail(activeLeftThumbnail);
      }
      
      // Handle regular thumbnails
      if (this.elements.thumbnails) {
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    setActiveLeftThumbnail(thumbnail) {
      if (!thumbnail) return;

      // Remove active state from all left thumbnails
      this.querySelectorAll('.left-thumbnail-item').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      
      // Add active state to current thumbnail
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      
      // Update arrow states
      this.updateLeftThumbnailsArrows();
    }

    navigateLeftThumbnails(direction) {
      const leftThumbnails = Array.from(this.querySelectorAll('.left-thumbnail-item[data-target]'));
      const currentActiveIndex = leftThumbnails.findIndex(thumb => thumb.classList.contains('is-active'));
      
      let nextIndex;
      if (direction === 'prev') {
        nextIndex = currentActiveIndex > 0 ? currentActiveIndex - 1 : leftThumbnails.length - 1;
      } else {
        nextIndex = currentActiveIndex < leftThumbnails.length - 1 ? currentActiveIndex + 1 : 0;
      }
      
      const nextThumbnail = leftThumbnails[nextIndex];
      if (nextThumbnail) {
        const mediaId = nextThumbnail.dataset.target;
        this.setActiveMedia(mediaId, false, true);
      }
    }

    updateLeftThumbnailsArrows() {
      const leftThumbnails = this.querySelectorAll('.left-thumbnail-item[data-target]');
      const currentActiveIndex = Array.from(leftThumbnails).findIndex(thumb => thumb.classList.contains('is-active'));
      const prevArrow = this.querySelector('.left-thumbnails-arrow--prev');
      const nextArrow = this.querySelector('.left-thumbnails-arrow--next');
      
      if (prevArrow && nextArrow) {
        // Disable prev arrow if on first image
        prevArrow.disabled = currentActiveIndex === 0;
        // Disable next arrow if on last image
        nextArrow.disabled = currentActiveIndex === leftThumbnails.length - 1;
      }
    }


    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
